import * as XLSX from 'xlsx'
import { SavWriter } from 'savfilewriter'
import CREON_CONTENT_RU_KZ from '../surveys/creonContentRuKz'
import CREON_CONTENT_UA from '../surveys/creonContentUa'

const STATUS_CODES = {
  completed: 1,
  screened_out: 2,
  quota_full: 3,
  bad_quality: 4,
  in_progress: 5,
}

const STATUS_LABELS = {
  1: 'Completed',
  2: 'Screened out',
  3: 'Quota full',
  4: 'Bad quality',
  5: 'In progress',
}

const METADATA_FIELDS = [
  { key: 'caseid', label: 'Response ID', type: 'string' },
  { key: 'survcode', label: 'Survey code', type: 'string' },
  { key: 'country', label: 'Country', type: 'string' },
  { key: 'segment', label: 'Respondent segment', type: 'string' },
  { key: 'bfrid', label: 'Panel / BFRID', type: 'string' },
  { key: 'partid', label: 'Participant ID', type: 'string' },
  { key: 'respstat', label: 'Interview status', type: 'number', values: STATUS_LABELS },
  { key: 'scrnout', label: 'Screenout reason', type: 'string' },
  { key: 'repact', label: 'Active in report', type: 'number', values: { 0: 'No', 1: 'Yes' } },
  { key: 'startedat', label: 'Interview started at (ISO 8601)', type: 'string' },
  { key: 'completed', label: 'Interview completed/updated at (ISO 8601)', type: 'string' },
  { key: 'lang', label: 'Interview language', type: 'string' },
]

const OPTION_SOURCE_BY_QUESTION = {
  'ET1.2': 'ET1.1',
  'ET1.3': 'ET1.1',
  'ET1.4': 'ET1.1',
  'ET2.2': 'ET2.1',
  'ET2.3': 'ET2.1',
  'ET2.4': 'ET2.1',
}

function textValue(value) {
  if (value === null || value === undefined) return ''
  return String(value)
}

function isoDate(value) {
  if (typeof value?.toDate === 'function') return value.toDate().toISOString()
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? textValue(value) : parsed.toISOString()
  }
  return ''
}

function byteLength(value) {
  return new TextEncoder().encode(textValue(value)).length
}

function truncateUtf8(value, maximum = 255) {
  const text = textValue(value)
  if (byteLength(text) <= maximum) return text
  let result = ''
  for (const character of text) {
    if (byteLength(result + character) > maximum) break
    result += character
  }
  return result
}

function utf8Part(value, partNumber) {
  const characters = [...textValue(value)]
  let bytesUsed = 0
  let currentPart = ''
  let currentPartNumber = 1
  for (const character of characters) {
    const characterBytes = byteLength(character)
    if (bytesUsed + characterBytes > 255) {
      if (currentPartNumber === partNumber) return currentPart
      currentPartNumber += 1
      bytesUsed = 0
      currentPart = ''
    }
    currentPart += character
    bytesUsed += characterBytes
  }
  return currentPartNumber === partNumber ? currentPart : ''
}

function questionSourceId(answerKey) {
  const normalized = textValue(answerKey)
    .replace(/_OTHER$/, '')
    .replace(/_(A|B)$/, '')
  return normalized.replace(/^ET(\d)_(\d)/, 'ET$1.$2')
}

function contentForSurvey(surveyCode) {
  if (['gen_z_mk_neuro', 'gen_z_rs_neuro', 'gen_z_xk_neuro', 'gen_z_me_neuro'].includes(surveyCode)) return {}
  return textValue(surveyCode).includes('_ua') ? CREON_CONTENT_UA : CREON_CONTENT_RU_KZ
}

function parseQuestion(content, questionId) {
  const sourceId = OPTION_SOURCE_BY_QUESTION[questionId] || questionId
  const entry = content[sourceId]
  if (!entry) return { question: '', options: {} }
  const sourceText = entry.en || entry.ua || entry.ru || entry.kz || ''
  const lines = textValue(sourceText).split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
  const optionMatcher = /^(\d+)(?:\.\s*|(?=[^\d.\s]))(.*)$/
  const firstOption = lines.findIndex((line) => optionMatcher.test(line))
  const question = (firstOption < 0 ? lines : lines.slice(0, firstOption))
    .filter((line) => !/^_+$/.test(line))
    .join(' ')
  const options = Object.fromEntries((firstOption < 0 ? [] : lines.slice(firstOption))
    .map((line) => line.match(optionMatcher))
    .filter(Boolean)
    .map((match) => [match[1], match[2]]))
  return { question, options }
}

function isNumeric(value) {
  return value !== '' && value !== null && value !== undefined && Number.isFinite(Number(value))
}

function createFieldKey(answerKey, optionCode = '') {
  return optionCode ? `${answerKey}__${optionCode}` : answerKey
}

function compactVariableName(sourceKey, usedNames) {
  const cleaned = textValue(sourceKey)
    .replace(/__/, '_')
    .replace(/[^A-Za-z0-9]/g, '')
    .replace(/^([^A-Za-z])/, 'Q$1')
    .toUpperCase()
  const base = (cleaned || 'Q').slice(0, 8)
  let candidate = base
  let suffix = 1
  while (usedNames.has(candidate)) {
    const suffixText = String(suffix)
    candidate = `${base.slice(0, 8 - suffixText.length)}${suffixText}`
    suffix += 1
  }
  usedNames.add(candidate)
  return candidate
}

function answerLabelFor(response, answerKey, code) {
  const labelled = response.answer_labels?.[answerKey]
  const raw = response.answers?.[answerKey]
  if (Array.isArray(raw) && Array.isArray(labelled)) {
    const index = raw.map(textValue).indexOf(textValue(code))
    return index >= 0 ? textValue(labelled[index]) : ''
  }
  if (!Array.isArray(raw) && !Array.isArray(labelled) && textValue(raw) === textValue(code)) return textValue(labelled)
  return ''
}

function responseMetadata(response) {
  return {
    caseid: response.response_id || response.source_response_id || response.id || '',
    survcode: response.survey_code || '',
    country: response.country || '',
    segment: response.segment || '',
    bfrid: response.bfrid || '',
    partid: response.participant_id || '',
    respstat: STATUS_CODES[response.status] || null,
    scrnout: response.screenout_reason || '',
    repact: response.reportActive === false ? 0 : 1,
    startedat: isoDate(response.started_at),
    completed: isoDate(response.completed_at || response.updated_at),
    lang: response.language || '',
  }
}

function buildDataset(responses) {
  const fields = new Map()
  const flattened = responses.map((response) => {
    const values = responseMetadata(response)
    Object.entries(response.answers || {}).forEach(([answerKey, answer]) => {
      const questionId = questionSourceId(answerKey)
      const definition = parseQuestion(contentForSurvey(response.survey_code), questionId)
      const addField = (fieldKey, extra = {}) => {
        if (!fields.has(fieldKey)) {
          fields.set(fieldKey, {
            fieldKey,
            answerKey,
            questionId,
            question: definition.question || `Response to ${answerKey}`,
            optionLabels: { ...definition.options },
            values: {},
            ...extra,
          })
        }
        return fields.get(fieldKey)
      }

      if (Array.isArray(answer)) {
        const codes = new Set([...Object.keys(definition.options), ...answer.map(textValue)])
        codes.forEach((code) => {
          const field = addField(createFieldKey(answerKey, code), {
            kind: 'multiple response',
            optionCode: code,
            optionLabel: definition.options[code] || answerLabelFor(response, answerKey, code) || code,
            valueType: 'number',
          })
          field.optionLabels[code] = field.optionLabel
          values[field.fieldKey] = answer.map(textValue).includes(code) ? 1 : 0
        })
        return
      }

      if (answer && typeof answer === 'object') {
        Object.entries(answer).forEach(([itemCode, itemValue]) => {
          const field = addField(createFieldKey(answerKey, itemCode), {
            kind: 'matrix item',
            itemCode,
            itemLabel: definition.options[itemCode] || `Item ${itemCode}`,
            valueType: isNumeric(itemValue) ? 'number' : 'string',
          })
          if (field.valueType === 'string' && isNumeric(itemValue)) field.valueType = 'number'
          values[field.fieldKey] = itemValue
        })
        return
      }

      const field = addField(createFieldKey(answerKey), {
        kind: answerKey.endsWith('_OTHER') ? 'open text' : 'single response',
        valueType: isNumeric(answer) ? 'number' : 'string',
      })
      if (field.valueType === 'string' && isNumeric(answer)) field.valueType = 'number'
      if (field.valueType === 'number' && Object.keys(definition.options).length) {
        field.values = { ...definition.options, ...field.values }
      }
      const label = answerLabelFor(response, answerKey, answer)
      if (label) field.values[textValue(answer)] = label
      values[field.fieldKey] = answer
    })
    return values
  })

  const usedNames = new Set(METADATA_FIELDS.map((field) => field.key.toUpperCase()))
  const answerFields = [...fields.values()].flatMap((field) => {
    const stringField = field.valueType === 'string'
    const maximumBytes = stringField ? Math.max(...flattened.map((values) => byteLength(values[field.fieldKey]))) : 0
    const parts = Math.max(1, Math.ceil(maximumBytes / 255))
    return Array.from({ length: parts }, (_, index) => ({
      ...field,
      fieldKey: parts > 1 ? `${field.fieldKey}__TEXT${index + 1}` : field.fieldKey,
      sourceFieldKey: field.fieldKey,
      textPart: parts > 1 ? index + 1 : null,
      textParts: parts > 1 ? parts : null,
    }))
  }).map((field) => ({
    ...field,
    variable: compactVariableName(field.fieldKey, usedNames),
  }))
  const allFields = [...METADATA_FIELDS.map((field) => ({ ...field, variable: field.key })), ...answerFields]
  const records = flattened.map((values) => Object.fromEntries(allFields.map((field) => {
    const rawValue = values[field.sourceFieldKey || field.fieldKey || field.key]
    const value = field.textPart
      ? utf8Part(rawValue, field.textPart)
      : field.type === 'string' || field.valueType === 'string' ? truncateUtf8(rawValue) : rawValue
    return [field.variable, value === undefined ? null : value]
  })))

  return { fields: allFields, records }
}

function spssValues(field) {
  if (field.values && Object.keys(field.values).length) return field.values
  if (field.kind === 'multiple response') return { 0: 'Not selected', 1: 'Selected' }
  return undefined
}

function variableLabel(field) {
  if (field.kind === 'multiple response') return `${field.question} — ${field.optionCode}: ${field.optionLabel}`
  if (field.kind === 'matrix item') return `${field.question} — ${field.itemCode}: ${field.itemLabel}`
  const label = field.label || field.question || field.answerKey
  return field.textPart ? `${label} (text part ${field.textPart}/${field.textParts})` : label
}

function dataMapRows(fields) {
  return fields.map((field) => {
    const values = spssValues(field)
    const valueLabels = values ? Object.entries(values).map(([code, label]) => `${code} = ${label}`).join('; ') : ''
    return {
      Variable: field.variable,
      'Question code': field.questionId || 'SYSTEM',
      'Question / field': variableLabel(field),
      'Response format': field.type === 'string' || field.valueType === 'string' ? 'Text' : 'Numeric',
      'Codes / value labels': valueLabels,
      Notes: field.kind === 'multiple response'
        ? 'One binary column per response option; blank means question was not shown or not answered.'
        : field.textPart ? 'Long text is split across sequential fields to preserve all UTF-8 characters in SPSS.' : '',
    }
  })
}

function makeWorksheet(rows, widths) {
  const worksheet = XLSX.utils.json_to_sheet(rows)
  worksheet['!cols'] = widths.map((width) => ({ wch: width }))
  worksheet['!autofilter'] = rows.length ? { ref: XLSX.utils.encode_range({ s: { c: 0, r: 0 }, e: { c: Object.keys(rows[0]).length - 1, r: rows.length } }) } : undefined
  return worksheet
}

function filenameBase(surveyCode) {
  return `${surveyCode === 'all' ? 'fusion-all-surveys' : surveyCode}-data`
}

export function exportFusionExcel(responses, surveyCode) {
  const { fields, records } = buildDataset(responses)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, makeWorksheet(records, fields.map(() => 18)), 'Data')
  XLSX.utils.book_append_sheet(workbook, makeWorksheet(dataMapRows(fields), [14, 16, 55, 16, 60, 52]), 'DataMap')
  XLSX.writeFile(workbook, `${filenameBase(surveyCode)}.xlsx`)
}

export function exportFusionSpss(responses, surveyCode) {
  const { fields, records } = buildDataset(responses)
  const sysvars = fields.map((field) => {
    const stringField = field.type === 'string' || field.valueType === 'string'
    const maximumWidth = Math.max(1, ...records.map((record) => byteLength(record[field.variable])))
    return {
      name: field.variable,
      type: stringField ? Math.min(255, maximumWidth) : 0,
      label: truncateUtf8(variableLabel(field)),
      printFormat: stringField ? `A${Math.min(255, maximumWidth)}` : 'F12.0',
      writeFormat: stringField ? `A${Math.min(255, maximumWidth)}` : 'F12.0',
      ...(spssValues(field) ? { values: spssValues(field) } : {}),
    }
  })
  SavWriter.download({
    encoding: 'UTF-8',
    fileLabel: 'Fusion survey export',
    sysvars,
  }, records, `${filenameBase(surveyCode)}.sav`)
}
